import { Bell, BellRing, Check, Copy, Ellipsis, Lock, Globe, Network, Pencil, Share2, SquarePen, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useStore } from "@/model/store";
import type { ModelNode, Source } from "@/model/types";
import { IconPicker, NodeIcon } from "@/components/NodeIcon";
import { ShareDialog, type ShareInfo } from "./ShareDialog";
import { useFollow } from "./useFollow";

const SOURCE_LABEL: Record<Source, string> = {
	manual: "Entered manually",
	market: "Follows market",
	crowd: "Follows crowd",
	feed: "Data feed",
	formula: "Calculated",
};

export function SourceBadge({ source }: { source: Source }) {
	return (
		<Badge variant="outline" className="font-normal">
			{SOURCE_LABEL[source]}
		</Badge>
	);
}

/** The node's formula name, small, with a copy button. */
function FormulaCode({ code }: { code: string }) {
	const [copied, setCopied] = useState(false);
	return (
		<button
			type="button"
			className="inline-flex items-center gap-1.5 rounded-md border bg-card px-1.5 py-0.5 font-mono text-[11px] text-foreground/80 hover:bg-muted"
			title="Copy to use in formulas"
			aria-label={`Copy formula name ${code}`}
			onClick={() => {
				void navigator.clipboard?.writeText(code);
				setCopied(true);
				setTimeout(() => setCopied(false), 1500);
			}}
		>
			{code}
			{copied ? <Check className="size-3 text-[var(--gain)]" /> : <Copy className="size-3 text-muted-foreground" />}
		</button>
	);
}

/** The question adds nothing when it only repeats the title. */
function sameText(a: string, b: string) {
	const norm = (s: string) =>
		s
			.trim()
			.toLowerCase()
			.replace(/[?.!\s]+$/, "");
	return norm(a) === norm(b);
}

export function NodeHeader({ node, share, onForecast }: { node: ModelNode; share?: ShareInfo; onForecast?: () => void }) {
	const [editing, setEditing] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [sharing, setSharing] = useState(false);
	const update = useStore((s) => s.updateNode);
	const setSource = useStore((s) => s.setSource);
	const del = useStore((s) => s.deleteNode);
	const analyst = useStore((s) => s.analyst);
	const navigate = useNavigate();
	const [draft, setDraft] = useState(node);
	const [followed, toggleFollow] = useFollow(node.id);

	const isPublic = node.visibility === "public";
	// Editors edit and delete; viewers read, share and follow (PRD §17.5). Every node in a workspace is its own in v1.
	const owner = useStore((s) => s.workspaces.find((w) => w.slug === s.ws)?.role !== "viewer");
	const openEdit = () => {
		setDraft(node);
		setEditing(true);
	};
	const showQuestion = node.question.trim() && !sameText(node.question, node.title);

	const overflow = (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon-sm" aria-label="More actions">
					<Ellipsis className="size-4" />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				{owner && isPublic && (
					<DropdownMenuItem onSelect={openEdit}>
						<Pencil className="size-4" /> Edit details
					</DropdownMenuItem>
				)}
				<DropdownMenuItem onSelect={() => navigate(`/graph?focus=${encodeURIComponent(node.id)}`)}>
					<Network className="size-4" /> Show on graph
				</DropdownMenuItem>
				{owner && (
					<>
						<DropdownMenuSeparator />
						<DropdownMenuItem variant="destructive" onSelect={() => setConfirmDelete(true)}>
							<Trash2 className="size-4" /> Delete {node.kind}
						</DropdownMenuItem>
					</>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	);

	return (
		<header className="mb-8">
			<div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
				<span className="inline-flex items-center gap-1">
					{node.visibility === "private" ? <Lock className="size-3.5" /> : <Globe className="size-3.5" />}
					{node.visibility === "private" ? "Private" : "Public"} {node.kind}
				</span>
				<SourceBadge source={node.source} />
				{node.aiDraft && (
					<Badge variant="outline" className="font-normal" title="Saved by an AI app and not yet saved by a person here">
						AI draft
					</Badge>
				)}
				{node.kind === "event" && node.happened && <Badge className="bg-[var(--gain)] font-normal text-white">Happened</Badge>}
				{analyst && <FormulaCode code={node.kind === "metric" ? node.id : `happened(${node.id})`} />}
				<div className="ml-auto flex flex-wrap items-center gap-1">
					{isPublic ? (
						<>
							{share && (
								<Button variant="ghost" size="sm" onClick={() => setSharing(true)}>
									<Share2 className="size-3.5" /> Share
								</Button>
							)}
							<Button variant="ghost" size="sm" aria-pressed={followed} onClick={toggleFollow}>
								{followed ? <BellRing className="size-3.5" /> : <Bell className="size-3.5" />} {followed ? "Following" : "Follow"}
							</Button>
							{onForecast && (
								<Button variant="outline" size="sm" onClick={onForecast}>
									<SquarePen className="size-3.5" /> Add your forecast
								</Button>
							)}
						</>
					) : (
						<Button variant="ghost" size="sm" onClick={openEdit}>
							<Pencil className="size-3.5" /> Edit details
						</Button>
					)}
					{overflow}
				</div>
			</div>
			<h1 className="mt-2 text-3xl font-semibold tracking-tight">
				<NodeIcon node={node} className="mr-3 text-[0.8em] text-muted-foreground" />
				{node.title}
			</h1>
			{showQuestion && <p className="mt-2 max-w-[65ch] font-serif text-lg leading-snug text-foreground/80">{node.question}</p>}
			{node.resolution.criteria ? (
				<p className="mt-2 max-w-[70ch] text-sm text-muted-foreground">
					{node.kind === "event" ? "Decided by" : "Measured by"}: {node.resolution.criteria}
					{node.resolution.source && ` Source: ${node.resolution.source}.`}
				</p>
			) : (
				// Only public nodes are scored (PRD §13.2), so only they need the prompt.
				isPublic && (
					<p className="mt-2 max-w-[70ch] text-sm text-muted-foreground">
						{node.kind === "event" ? "Decided by" : "Measured by"}: not set, so it can’t be scored.{" "}
						{owner && (
							<button type="button" className="text-foreground underline underline-offset-2 hover:no-underline" onClick={openEdit}>
								Add it
							</button>
						)}
					</p>
				)
			)}
			{share && <ShareDialog open={sharing} onOpenChange={setSharing} node={node} info={share} />}

			<Dialog open={editing} onOpenChange={setEditing}>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>Edit details</DialogTitle>
					</DialogHeader>
					<div className="space-y-3">
						<div className="grid grid-cols-[4rem_1fr] gap-3">
							<div className="space-y-1.5">
								<Label>Icon</Label>
								<IconPicker value={draft.icon} onChange={(icon) => setDraft({ ...draft, icon })} kind={node.kind} />
							</div>
							<div className="space-y-1.5">
								<Label>Title</Label>
								<Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
							</div>
						</div>
						<div className="space-y-1.5">
							<Label>{node.kind === "event" ? "Question" : "What it measures"}</Label>
							<Textarea value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} />
						</div>
						<div className="space-y-1.5">
							<Label>{node.kind === "event" ? "How it’s decided" : "How it’s measured"}</Label>
							<Textarea
								value={draft.resolution.criteria}
								onChange={(e) => setDraft({ ...draft, resolution: { ...draft.resolution, criteria: e.target.value } })}
							/>
						</div>
						<div className="grid grid-cols-2 gap-3">
							<div className="space-y-1.5">
								<Label>{node.kind === "event" ? "Source of the decision" : "Data source"}</Label>
								<Input value={draft.resolution.source} onChange={(e) => setDraft({ ...draft, resolution: { ...draft.resolution, source: e.target.value } })} />
							</div>
							<div className="space-y-1.5">
								<Label>Where the numbers come from</Label>
								<Select value={draft.source} onValueChange={(v) => setDraft({ ...draft, source: v as Source })} disabled={draft.source === "formula"}>
									<SelectTrigger className="w-full">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{(["manual", "market", "crowd", "feed", "formula"] as Source[]).map((s) => (
											<SelectItem key={s} value={s} disabled={s === "formula"}>
												{SOURCE_LABEL[s]}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
						<div className="space-y-1.5">
							<Label>Visibility</Label>
							{/* A public label can become private; a private node stays private (D11), so "Public" is offered only while it is public. */}
							<Select
								value={draft.visibility}
								disabled={node.visibility === "private"}
								onValueChange={(v) => setDraft({ ...draft, visibility: v as "public" | "private" })}
							>
								<SelectTrigger className="w-full">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="private">Private: this workspace only</SelectItem>
									<SelectItem value="public" disabled={node.visibility === "private"}>
										Public: in the shared library
									</SelectItem>
								</SelectContent>
							</Select>
							<p className="text-xs text-muted-foreground">
								{node.visibility === "private"
									? `A private ${node.kind} stays private: its data never leaves this workspace.`
									: `Make it private to keep it in this workspace only. A private ${node.kind} can’t be made public again.`}
							</p>
						</div>
					</div>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setEditing(false)}>
							Cancel
						</Button>
						<Button
							onClick={() => {
								void update(node.id, {
									title: draft.title,
									icon: draft.icon,
									question: draft.question,
									resolution: draft.resolution,
									...(draft.visibility !== node.visibility ? { visibility: draft.visibility } : {}),
								});
								if (draft.source !== node.source) void setSource(node.id, draft.source);
								setEditing(false);
							}}
						>
							Save details
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Delete {node.title}?</DialogTitle>
					</DialogHeader>
					<p className="text-sm text-muted-foreground">Its links and scenario levers are removed too. This can be undone.</p>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setConfirmDelete(false)}>
							Keep it
						</Button>
						<Button
							variant="destructive"
							onClick={() => {
								void del(node.id).then((ok) => {
									setConfirmDelete(false);
									if (ok) navigate("/");
								});
							}}
						>
							Delete {node.kind}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</header>
	);
}
